// @ts-nocheck
import { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, type CarouselItem, type CarouselProgress } from "@/lib/api";

const LLM_BUSY = [
  "concepts",
  "discuss",
  "copy",
  "review",
  "retro",
  "layout_plan",
  "inspiration",
  "layout_qa",
  "draft_critique",
  "draft_place",
  "draft_caption",
  "caption_cta",
  "design_review",
  "design_verify",
  "czasotekst_copy",
  "feed_post_copy",
  "czasotekst_chat",
  "copy_chat",
];

function progressBusy(d?: CarouselProgress | null): boolean {
  if (!d) return false;
  if (LLM_BUSY.includes(d.llm_status || "")) return true;
  const st = d.render_status;
  if (st === "queued" || st === "rendering" || st === "design") return true;
  if (d.reel_status === "queued" || d.reel_status === "rendering") return true;
  if (d.status === "publishing" || d.publish_progress?.phase === "publishing") return true;
  const prop = d.propagate_progress?.phase;
  if (prop && !["done", "failed", "error", "idle", ""].includes(prop)) return true;
  const bg = d.bg_cand_progress?.phase;
  if (bg && !["done", "failed", "error", "idle", ""].includes(bg)) return true;
  return false;
}

function progressFingerprint(d: CarouselProgress): string {
  return [
    d.status || "",
    d.llm_status || "",
    d.llm_detail || "",
    d.render_status || "",
    String(d.render_done ?? ""),
    d.reel_status || "",
    d.propagate_progress?.phase || "",
    d.bg_cand_progress?.phase || "",
    d.publish_progress?.phase || "",
    d.llm_progress?.phase || "",
    d.llm_progress?.last_event || "",
    d.llm_progress?.hint || "",
    String(d.llm_progress?.awaiting_text_sec ?? ""),
    String(d.llm_progress?.received_chars ?? ""),
    d.render_progress?.phase || "",
    String(d.llm_progress?.percent ?? ""),
    String(d.render_progress?.percent ?? ""),
  ].join("|");
}

/** Fingerprint that warrants a full carousel GET (content may have changed). */
function contentFingerprint(d: CarouselProgress): string {
  return [
    d.status || "",
    d.llm_status || "",
    d.render_status || "",
    String(d.render_done ?? ""),
    d.reel_status || "",
    d.propagate_progress?.phase || "",
    d.bg_cand_progress?.phase || "",
    d.publish_progress?.phase || "",
  ].join("|");
}

function mergeProgressIntoItem(old: CarouselItem | undefined, p: CarouselProgress): CarouselItem | undefined {
  if (!old) return old;
  return {
    ...old,
    updated_at: p.updated_at || old.updated_at,
    status: p.status || old.status,
    llm_status: p.llm_status ?? old.llm_status,
    llm_detail: p.llm_detail ?? old.llm_detail,
    llm_progress: p.llm_progress ?? old.llm_progress,
    render_status: p.render_status ?? old.render_status,
    render_error: p.render_error ?? old.render_error,
    render_done: p.render_done ?? old.render_done,
    render_total: p.render_total ?? old.render_total,
    render_progress: p.render_progress ?? old.render_progress,
    render_token: p.render_token ?? old.render_token,
    propagate_progress: p.propagate_progress ?? old.propagate_progress,
    bg_cand_progress: p.bg_cand_progress ?? old.bg_cand_progress,
    publish_progress: p.publish_progress ?? old.publish_progress,
    reel_status: p.reel_status ?? old.reel_status,
    reel_error: p.reel_error ?? old.reel_error,
    reel_progress: p.reel_progress ?? old.reel_progress,
    celery_task_id: p.celery_task_id ?? old.celery_task_id,
  };
}

export function useCarouselQueries({ pain, book, tool, useBook, useComments, useMessenger, useStories, aiJob, selectedId }: any) {
  const qc = useQueryClient();
  const [pollMs, setPollMs] = useState(700);
  const sameFpCount = useRef(0);
  const lastFp = useRef("");
  const lastContentFp = useRef("");
  const busySince = useRef(0);

  const catalogQ = useQuery({ queryKey: ["carousel-catalog"], queryFn: api.carouselCatalog });
  const sourcesQ = useQuery({
    queryKey: ["carousel-sources", pain, book, tool, useBook, useComments, useMessenger, useStories],
    queryFn: () =>
      api.carouselSources({
        pain_id: pain,
        book_id: book,
        tool_id: tool,
        use_book: useBook,
        use_comments: useComments,
        use_messenger: useMessenger,
        use_stories: useStories,
      }),
  });
  const statusQ = useQuery({ queryKey: ["carousel-status"], queryFn: api.carouselStatus });
  const listQ = useQuery({
    queryKey: ["carousels"],
    queryFn: () => api.carouselList(),
  });
  const listSummaryQ = useQuery({
    queryKey: ["carousels-summary"],
    queryFn: () => api.carouselList({ view: "summary" }),
    refetchInterval: (query) => {
      if (aiJob === "publish") return 2000;
      const rows = query.state.data?.items || [];
      return rows.some((row) => row.status === "publishing") ? 2000 : false;
    },
  });
  const rankingQ = useQuery({ queryKey: ["carousel-ranking"], queryFn: api.carouselRanking });
  const musicStatusQ = useQuery({ queryKey: ["carousel-music-status"], queryFn: api.carouselMusicStatus });
  const proposeMusicQ = useQuery({
    queryKey: ["carousel-music-propose", selectedId],
    queryFn: () => api.carouselMusicPropose(selectedId!),
    enabled: !!selectedId,
    retry: false,
  });
  const slotQ = useQuery({
    queryKey: ["carousel-publish-slot", selectedId],
    queryFn: () => api.carouselPublishSlot(selectedId || undefined),
  });

  const currentQ = useQuery({
    queryKey: ["carousel", selectedId],
    queryFn: () => api.carouselGet(selectedId!),
    enabled: !!selectedId,
  });

  const progressQ = useQuery({
    queryKey: ["carousel-progress", selectedId],
    queryFn: () => api.carouselProgress(selectedId!),
    enabled: !!selectedId,
    refetchInterval: (query) => {
      const d = query.state.data;
      if (!progressBusy(d)) {
        busySince.current = 0;
        return false;
      }
      if (!busySince.current) busySince.current = Date.now();
      // Stop aggressive poll after 15 min of continuous busy (stuck job).
      if (Date.now() - busySince.current > 15 * 60 * 1000) return 30_000;
      return pollMs;
    },
  });

  useEffect(() => {
    if (!selectedId || !progressQ.data) return;
    const p = progressQ.data;
    const fp = progressFingerprint(p);
    const cfp = contentFingerprint(p);

    qc.setQueryData(["carousel", selectedId], (old: CarouselItem | undefined) => mergeProgressIntoItem(old, p));

    if (lastContentFp.current && lastContentFp.current !== cfp) {
      void qc.invalidateQueries({ queryKey: ["carousel", selectedId] });
    }
    lastContentFp.current = cfp;

    if (fp === lastFp.current) {
      sameFpCount.current += 1;
      if (sameFpCount.current > 15) setPollMs(5000);
      else if (sameFpCount.current > 5) setPollMs(2000);
    } else {
      sameFpCount.current = 0;
      setPollMs(700);
      lastFp.current = fp;
    }
  }, [progressQ.dataUpdatedAt, selectedId, qc, progressQ.data]);

  // Merge summary poll into full list cache for sidebar status badges during publish.
  useEffect(() => {
    if (!listSummaryQ.data?.items?.length) return;
    qc.setQueryData(["carousels"], (old: { items?: CarouselItem[]; count?: number } | undefined) => {
      if (!old?.items?.length) return { items: listSummaryQ.data.items, count: listSummaryQ.data.count };
      const byId = new Map(listSummaryQ.data.items.map((r) => [r.id, r]));
      return {
        ...old,
        items: old.items.map((row) => {
          const s = byId.get(row.id);
          if (!s) return row;
          return {
            ...row,
            status: s.status || row.status,
            title: s.title || row.title,
            hook: s.hook || row.hook,
            updated_at: s.updated_at || row.updated_at,
            llm_status: s.llm_status ?? row.llm_status,
            render_status: s.render_status ?? row.render_status,
            preview_kind: s.preview_kind ?? row.preview_kind,
            preview_url: s.preview_url ?? row.preview_url,
            rating: s.rating ?? row.rating,
            ig_permalink: s.ig_permalink ?? row.ig_permalink,
            fb_permalink: s.fb_permalink ?? row.fb_permalink,
          };
        }),
      };
    });
  }, [listSummaryQ.dataUpdatedAt, listSummaryQ.data, qc]);

  return {
    catalogQ,
    sourcesQ,
    statusQ,
    listQ,
    listSummaryQ,
    rankingQ,
    musicStatusQ,
    proposeMusicQ,
    slotQ,
    currentQ,
    progressQ,
  };
}
